import { useState } from "react";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { format } from "date-fns";
import { Receipt } from "@untitledui/icons";
import {
  learningGetPurchaseReceipt,
  useLearningListPurchases,
} from "@/services/api/learning-purchases/learning-purchases";
import { EmptyState } from "@/components/application/empty-state/empty-state";
import { Button } from "@/components/base/buttons/button";
import { formatCoursePrice } from "@/utils/course-formatters";
import { fetchFeatureFlags } from "@/hooks/useFeatureFlags";

export const Route = createFileRoute("/_app/my-purchases")({
  // Purchase history is hidden while the payments feature flag is off (the API 404s it too).
  beforeLoad: async () => {
    const { payments } = await fetchFeatureFlags();
    if (!payments) throw redirect({ to: "/my-learning" });
  },
  component: MyPurchases,
});

function MyPurchases() {
  const { data, isLoading, isError } = useLearningListPurchases();
  const purchases = data?.items ?? [];

  // Track the receipt action per row so only the activated purchase shows a spinner / error.
  const [receiptPendingId, setReceiptPendingId] = useState<string | null>(null);
  const [receiptErrorId, setReceiptErrorId] = useState<string | null>(null);

  const handleViewReceipt = async (purchaseId: string) => {
    setReceiptErrorId(null);
    setReceiptPendingId(purchaseId);
    try {
      const { receiptUrl } = await learningGetPurchaseReceipt(purchaseId);
      window.open(receiptUrl, "_blank", "noopener,noreferrer");
    } catch {
      setReceiptErrorId(purchaseId);
    } finally {
      setReceiptPendingId(null);
    }
  };

  return (
    <main className="mx-auto max-w-[1280px] px-8 py-8">
      <h1 className="text-arc-gray-900 mb-2 text-4xl font-semibold">My Purchases</h1>
      <p className="text-arc-gray-600 mb-8 text-base">
        A record of the courses you've bought, with a receipt for each.
      </p>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-arc-gray-100 h-24 animate-pulse rounded-2xl" />
          ))}
        </div>
      ) : isError ? (
        <div className="text-arc-error-600 py-8 text-center">
          Failed to load your purchases. Please try again.
        </div>
      ) : purchases.length === 0 ? (
        <EmptyState>
          <EmptyState.Header>
            <EmptyState.FeaturedIcon icon={Receipt} color="gray" />
          </EmptyState.Header>
          <EmptyState.Content>
            <EmptyState.Title>No purchases yet</EmptyState.Title>
            <EmptyState.Description>You haven't bought any courses yet.</EmptyState.Description>
          </EmptyState.Content>
        </EmptyState>
      ) : (
        <ul className="divide-arc-gray-200 border-arc-gray-200 divide-y rounded-2xl border">
          {purchases.map((purchase) => (
            <li
              key={purchase.purchaseId}
              className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between"
            >
              <div className="flex flex-col gap-1">
                <span className="text-arc-gray-900 text-lg font-semibold">
                  {purchase.courseTitle}
                </span>
                <span className="text-arc-gray-600 text-sm">
                  {formatCoursePrice(purchase.amountPence, purchase.currency)} ·{" "}
                  {format(new Date(purchase.purchasedAt), "d MMMM yyyy")}
                </span>
                {receiptErrorId === purchase.purchaseId ? (
                  <span className="text-arc-error-600 text-sm" role="alert">
                    Receipt unavailable. Please try again later.
                  </span>
                ) : null}
              </div>
              <Button
                color="secondary"
                size="sm"
                iconLeading={Receipt}
                onClick={() => handleViewReceipt(purchase.purchaseId)}
                isLoading={receiptPendingId === purchase.purchaseId}
                isDisabled={receiptPendingId === purchase.purchaseId}
              >
                View receipt
              </Button>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
